import { useEffect, useState, type ReactNode } from "react";
import {
  ChevronsUpDown,
  FileClock,
  LogOut,
  Moon,
  Settings,
  Sun,
  User,
  Users,
} from "lucide-react";
import { CanAccess, useGetIdentity, useLogout, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { useTheme } from "@/components/admin/use-theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { ChangelogPage } from "@/components/atomic-crm/misc/ChangelogPage";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

import { useCloseMobile } from "./useCloseMobile";
import { useSystemDark } from "./useSystemDark";

/** Sign-in address from the current session; the sales row carries no email in the identity. */
const useSessionEmail = () => {
  const [email, setEmail] = useState("");
  useEffect(() => {
    const { data } = getSupabaseClient().auth.onAuthStateChange(
      (_event, session) => setEmail(session?.user?.email ?? ""),
    );
    return () => data.subscription.unsubscribe();
  }, []);
  return email;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** Account row at the foot of the sidebar: name and address, opening the account menu. */
export const VoltUserMenu = ({ changelog }: { changelog: boolean }) => {
  const translate = useTranslate();
  const logout = useLogout();
  const { identity } = useGetIdentity();
  const email = useSessionEmail();
  const { isMobile } = useSidebar();
  const closeMobile = useCloseMobile();
  const { pathname } = useLocation();
  const { theme, setTheme } = useTheme();
  const systemDark = useSystemDark();
  const dark = theme === "dark" || (theme === "system" && systemDark);
  const name = identity?.fullName ?? "";
  const account = translate("crm.navigation.account");
  const inMenu = [
    "/profile",
    "/sales",
    "/settings",
    ...(changelog ? [ChangelogPage.path] : []),
  ].some((path) => !!matchPath(`${path}/*`, pathname));

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Labelled "account" so the button says what it opens, not who is signed in. */}
            <SidebarMenuButton
              size="lg"
              isActive={inMenu}
              tooltip={account}
              aria-label={account}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {/* Initials stay in the neutral tone; the shared fallback would tint them by name. */}
              <Avatar aria-hidden="true">
                <AvatarImage src={identity?.avatar} alt="" />
                <AvatarFallback className="bg-muted! text-xs font-semibold text-muted-foreground">
                  {initials(name)}
                </AvatarFallback>
              </Avatar>
              {/* min-w-0 lets a long address truncate instead of widening the bar. */}
              <div className="grid min-w-0 flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {email}
                </span>
              </div>
              <ChevronsUpDown
                className="ml-auto text-muted-foreground group-data-[collapsible=icon]:hidden"
                aria-hidden="true"
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          {/* In the sheet there is no room beside the bar, so the menu opens downwards. */}
          <DropdownMenuContent
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
          >
            <MenuLink
              to="/profile"
              label={translate("crm.profile.title")}
              icon={<User />}
              onClick={closeMobile}
            />
            <CanAccess resource="sales" action="list">
              <MenuLink
                to="/sales"
                label={translate("resources.sales.name", { smart_count: 2 })}
                icon={<Users />}
                onClick={closeMobile}
              />
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <MenuLink
                to="/settings"
                label={translate("crm.settings.title")}
                icon={<Settings />}
                onClick={closeMobile}
              />
            </CanAccess>
            {changelog && (
              <MenuLink
                to={ChangelogPage.path}
                label={translate("crm.changelog.title")}
                icon={<FileClock />}
                onClick={closeMobile}
              />
            )}
            <DropdownMenuItem
              onSelect={() => setTheme(dark ? "light" : "dark")}
            >
              {dark ? <Sun /> : <Moon />}
              {translate(dark ? "crm.theme.light" : "crm.theme.dark")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout()}>
              <LogOut />
              {translate("ra.auth.logout")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => logout({ everywhere: true })}>
              <LogOut />
              Auf allen Geräten abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

const MenuLink = ({
  to,
  label,
  icon,
  onClick,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}) => (
  <DropdownMenuItem asChild>
    <Link to={to} onClick={onClick}>
      {icon}
      {label}
    </Link>
  </DropdownMenuItem>
);
